/**
 * Structured book model for native PDF export (#289–#295): chapters,
 * headings, openings, font ids, map leaves, and front matter. Not HTML.
 */
import type { Node as PMNode } from "prosemirror-model";
import type { EditorFontId } from "../editor/fonts";
import { assertNever } from "../lib/assertNever";
import { initialLength, leadInLength } from "../lib/chapterOpening";
import {
  fromMaybe,
  isJust,
  mapMaybe,
  matchMaybe,
  nothing,
  type Maybe,
} from "../lib/maybe";
import { sceneCustomName } from "../lib/sceneLabel";
import type {
  ZwChapter,
  ZwChapterHeadingStyle,
  ZwCopyrightPage,
  ZwDedicationPage,
  ZwMapFigure,
  ZwMetadata,
  ZwOpeningInitial,
  ZwPageChrome,
  ZwParagraphFormatting,
  ZwPrintSize,
  ZwSceneFormatting,
} from "../lib/zwConfig";
import {
  CHAPTER_TITLE_BASE_PT,
  chapterTitleFontSizePt,
} from "./chapterHeading";
import {
  type SceneOpening,
  PLAIN_SCENE_OPENING,
  sceneOpening,
} from "./chapterOpening";
import { buildCopyrightPageContent } from "./copyrightPageContent";
import { buildDedicationPageContent } from "./dedicationPage";
import {
  mapImagesForPrint,
  type MapExportImage,
  type PreparedMapFigure,
} from "./mapFigureExport";
import { DROP_CAP_EM, RAISED_INITIAL_EM } from "./paragraphStyle";
import { sceneBreakMarkerText } from "./sceneFormatting";
import { cssLengthToPx, printPageSizeFor } from "./printSizes";

export type PdfBookOpeningInitial = ZwOpeningInitial;

export type PdfBookMark = {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly smallCaps: boolean;
  readonly opening: PdfBookOpeningInitial;
};

const NO_MARKS: PdfBookMark = {
  bold: false,
  italic: false,
  smallCaps: false,
  opening: "none",
};

export type PdfBookRun = {
  readonly text: string;
  readonly marks: PdfBookMark;
};

export type PdfBookParagraph = {
  readonly type: "paragraph";
  readonly firstLineIndent: boolean;
  readonly runs: readonly PdfBookRun[];
};

export type PdfBookSceneBreak = {
  readonly type: "sceneBreak";
  readonly text: string;
};

export type PdfBookHeading = {
  readonly type: "heading";
  readonly level: 2;
  readonly runs: readonly PdfBookRun[];
};

export type PdfBookBlock =
  PdfBookParagraph | PdfBookSceneBreak | PdfBookHeading;

export type PdfBookTitleAlign = "left" | "center" | "right";

/** Raster for a chapter title image (Assets path resolved at export). */
export type PdfBookImage = {
  readonly mime: string;
  readonly bytesBase64: string;
  readonly widthPx: number;
  readonly heightPx: number;
};

/** Resolved chapter title layout (points), from project heading style. */
export type PdfBookChapterHeading = {
  readonly alignment: PdfBookTitleAlign;
  readonly sizePt: number;
  readonly spaceBeforePt: number;
  readonly spaceAfterPt: number;
  /** Assets-relative path; empty means none. */
  readonly imagePath: string;
  readonly imagePosition: "above" | "below";
  /** Set when export loads `imagePath` bytes. */
  readonly image: Maybe<PdfBookImage>;
};

export type PdfBookChapter = {
  readonly type: "chapter";
  readonly title: string;
  readonly heading: PdfBookChapterHeading;
  readonly blocks: readonly PdfBookBlock[];
};

/** Manuscript map leaf (#294). Orientation is already baked into the PNG(s). */
export type PdfBookMap = {
  readonly type: "map";
  readonly title: string;
  readonly printExtent: ZwMapFigure["printExtent"];
  readonly objectFit: ZwMapFigure["objectFit"];
  readonly pages:
    | { readonly kind: "single"; readonly image: PdfBookImage }
    | {
        readonly kind: "spread";
        readonly verso: PdfBookImage;
        readonly recto: PdfBookImage;
      };
};

export type PdfBookLeaf = PdfBookChapter | PdfBookMap;

export type PdfBookPageSize = {
  readonly widthPt: number;
  readonly heightPt: number;
};

export type PdfBookFonts = {
  readonly bodyId: EditorFontId;
  readonly headingId: EditorFontId;
};

export type PdfBookFrontParagraph = {
  readonly text: string;
  readonly kind: string;
};

export type PdfBookTitlePage = {
  readonly title: string;
  readonly subtitle: string;
  readonly author: string;
};

export type PdfBookCopyrightPage = {
  readonly paragraphs: readonly PdfBookFrontParagraph[];
};

export type PdfBookDedicationPage = {
  readonly paragraphs: readonly string[];
};

export type PdfBookFrontMatter = {
  readonly titlePage: PdfBookTitlePage;
  readonly copyright: Maybe<PdfBookCopyrightPage>;
  readonly dedication: Maybe<PdfBookDedicationPage>;
};

export type PdfBook = {
  readonly title: string;
  readonly author: string;
  readonly printSize: ZwPrintSize;
  readonly page: PdfBookPageSize;
  /** Body margin on each side, points. */
  readonly marginPt: number;
  readonly fonts: PdfBookFonts;
  readonly pageChrome: ZwPageChrome;
  readonly frontMatter: PdfBookFrontMatter;
  readonly body: readonly PdfBookLeaf[];
};

/** Exported for Rust-side layout parity docs / tests. */
export const PDF_BODY_SIZE_PT = 11;
export const PDF_DROP_CAP_EM = DROP_CAP_EM;
export const PDF_RAISED_INITIAL_EM = RAISED_INITIAL_EM;
export const PDF_CHAPTER_TITLE_BASE_PT = CHAPTER_TITLE_BASE_PT;

const BODY_MARGIN_IN = 0.75;

function cssLengthToPt(value: string): number {
  return (cssLengthToPx(value) / 96) * 72;
}

export function pdfBookPageSize(printSize: ZwPrintSize): PdfBookPageSize {
  const page = printPageSizeFor(printSize);
  return {
    widthPt: cssLengthToPt(page.width),
    heightPt: cssLengthToPt(page.height),
  };
}

export function resolvePdfBookChapterHeading(
  style: ZwChapterHeadingStyle,
): PdfBookChapterHeading {
  const sizePt = chapterTitleFontSizePt(style.scale);
  // Schema: spaceBefore/After are em relative to body size.
  const em = PDF_BODY_SIZE_PT;
  return {
    alignment: style.alignment,
    sizePt,
    spaceBeforePt: style.spaceBefore * em,
    spaceAfterPt: style.spaceAfter * em,
    imagePath: style.image.trim(),
    imagePosition: style.imagePosition,
    image: nothing,
  };
}

function marksFromPm(node: PMNode): PdfBookMark {
  let bold = false;
  let italic = false;
  node.marks.forEach((mark) => {
    if (mark.type.name === "bold" || mark.type.name === "strong") bold = true;
    if (mark.type.name === "italic" || mark.type.name === "em") italic = true;
  });
  return { bold, italic, smallCaps: false, opening: "none" };
}

function runsFromInline(node: PMNode): PdfBookRun[] {
  const runs: PdfBookRun[] = [];
  node.forEach((child) => {
    if (child.isText && typeof child.text === "string") {
      runs.push({ text: child.text, marks: marksFromPm(child) });
      return;
    }
    if (child.type.name === "hard_break") {
      runs.push({ text: "\n", marks: NO_MARKS });
      return;
    }
  });
  return runs;
}

type CharMark = {
  readonly ch: string;
  readonly bold: boolean;
  readonly italic: boolean;
};

function flattenRuns(runs: readonly PdfBookRun[]): CharMark[] {
  const out: CharMark[] = [];
  for (const run of runs) {
    for (const ch of run.text) {
      out.push({ ch, bold: run.marks.bold, italic: run.marks.italic });
    }
  }
  return out;
}

function regroupChars(
  chars: readonly CharMark[],
  markAt: (index: number) => PdfBookMark,
): PdfBookRun[] {
  if (chars.length === 0) return [];
  const runs: PdfBookRun[] = [];
  let start = 0;
  let marks = markAt(0);
  for (let i = 1; i <= chars.length; i++) {
    const next =
      i < chars.length
        ? markAt(i)
        : {
            bold: false,
            italic: false,
            smallCaps: false,
            opening: "none" as const,
          };
    const same =
      i < chars.length &&
      next.bold === marks.bold &&
      next.italic === marks.italic &&
      next.smallCaps === marks.smallCaps &&
      next.opening === marks.opening;
    if (same) continue;
    runs.push({
      text: chars
        .slice(start, i)
        .map((c) => c.ch)
        .join(""),
      marks,
    });
    start = i;
    marks = next;
  }
  return runs;
}

/**
 * Split the first paragraph's runs for chapter openings: lead-in small-caps
 * and drop/raised initial (same ranges as HTML `markSceneOpening`).
 */
export function applyOpeningToRuns(
  runs: readonly PdfBookRun[],
  opening: SceneOpening,
): PdfBookRun[] {
  if (opening.openingInitial === "none" && opening.openingLeadIn === "none") {
    return runs.map((r) => ({ text: r.text, marks: { ...r.marks } }));
  }
  const text = runs.map((r) => r.text).join("");
  const leadLen =
    opening.openingLeadIn === "none"
      ? 0
      : leadInLength(text, opening.openingLeadIn);
  const initLen = opening.openingInitial === "none" ? 0 : initialLength(text);
  const chars = flattenRuns(runs);
  return regroupChars(chars, (index) => {
    const base = chars[index]!;
    return {
      bold: base.bold,
      italic: base.italic,
      smallCaps: index < leadLen,
      opening: index < initLen ? opening.openingInitial : ("none" as const),
    };
  });
}

/** Top-level blocks from one scene `.zwdoc`. */
export function zwdocToPdfBookBlocks(
  doc: PMNode,
  firstParagraphIndent: boolean,
  opening: SceneOpening = PLAIN_SCENE_OPENING,
): PdfBookBlock[] {
  const blocks: PdfBookBlock[] = [];
  let isFirstParagraph = true;
  doc.forEach((child) => {
    if (child.type.name === "paragraph") {
      let runs = runsFromInline(child);
      const text = runs.map((r) => r.text).join("");
      if (text.trim() === "") {
        isFirstParagraph = false;
        return;
      }
      if (isFirstParagraph) {
        runs = applyOpeningToRuns(runs, opening);
      }
      const indent = isFirstParagraph ? firstParagraphIndent : true;
      blocks.push({
        type: "paragraph",
        firstLineIndent: indent,
        runs,
      });
      isFirstParagraph = false;
      return;
    }
    if (child.type.name === "heading") {
      blocks.push({
        type: "heading",
        level: 2,
        runs: runsFromInline(child),
      });
      isFirstParagraph = false;
    }
  });
  return blocks;
}

/**
 * Apply project paragraph style: when style is "spaced", still indent first
 * lines the same as indented mode for MVP (spacing is a vertical concern later).
 * `firstParagraph` flush rules come from `sceneOpening`.
 */
export function paragraphFirstLineIndent(
  formatting: ZwParagraphFormatting,
  flushOpening: boolean,
  isFirstInScene: boolean,
): boolean {
  if (formatting.style === "spaced") {
    if (isFirstInScene && flushOpening) return false;
    return true;
  }
  if (isFirstInScene && flushOpening) return false;
  return true;
}

export function chapterToPdfBookChapter(
  chapter: ZwChapter,
  scenes: readonly PMNode[],
  sceneFormatting: ZwSceneFormatting,
  paragraphFormatting: ZwParagraphFormatting,
  headingStyle: ZwChapterHeadingStyle,
): PdfBookChapter {
  const blocks: PdfBookBlock[] = [];
  const marker = sceneBreakMarkerText(sceneFormatting);

  scenes.forEach((doc, index) => {
    if (index > 0 && isJust(marker)) {
      blocks.push({ type: "sceneBreak", text: marker.value });
    }
    if (sceneFormatting.showSceneTitles === true) {
      matchMaybe(sceneCustomName(chapter.scenes[index]!), {
        just: (name) => {
          blocks.push({
            type: "heading",
            level: 2,
            runs: [{ text: name, marks: NO_MARKS }],
          });
        },
        nothing: () => {
          /* nameless scene: no heading */
        },
      });
    }
    const opening = sceneOpening(paragraphFormatting, index === 0);
    const sceneBlocks = zwdocToPdfBookBlocks(
      doc,
      paragraphFirstLineIndent(paragraphFormatting, opening.flush, true),
      opening,
    );
    for (const block of sceneBlocks) {
      blocks.push(block);
    }
  });

  return {
    type: "chapter",
    title: chapter.name,
    heading: resolvePdfBookChapterHeading(headingStyle),
    blocks,
  };
}

export type BuildPdfBookLeafInput =
  | {
      readonly type: "chapter";
      readonly chapter: ZwChapter;
      readonly scenes: readonly PMNode[];
    }
  | { readonly type: "map"; readonly prepared: PreparedMapFigure };

export type BuildPdfBookInput = {
  readonly title: string;
  readonly author: string;
  readonly subtitle?: string;
  readonly printSize: ZwPrintSize;
  readonly paragraphFormatting: ZwParagraphFormatting;
  readonly sceneFormatting: ZwSceneFormatting;
  readonly chapterHeadingStyle: ZwChapterHeadingStyle;
  readonly pageChrome: ZwPageChrome;
  readonly bodyFontId: EditorFontId;
  readonly headingFontId: EditorFontId;
  readonly metadata: ZwMetadata;
  readonly copyrightPage: ZwCopyrightPage;
  readonly dedicationPage: ZwDedicationPage;
  readonly leaves: readonly BuildPdfBookLeafInput[];
};

/** Title / copyright / dedication for the native PDF path. */
export function buildPdfBookFrontMatter(input: {
  readonly title: string;
  readonly author: string;
  readonly subtitle: string;
  readonly metadata: ZwMetadata;
  readonly copyrightPage: ZwCopyrightPage;
  readonly dedicationPage: ZwDedicationPage;
}): PdfBookFrontMatter {
  const metaTitle = input.metadata.title.trim();
  const metaAuthor = input.metadata.author.trim();
  const title = metaTitle !== "" ? metaTitle : input.title;
  const author = metaAuthor !== "" ? metaAuthor : input.author;
  const subtitle =
    input.metadata.subtitle.trim() !== ""
      ? input.metadata.subtitle.trim()
      : input.subtitle.trim();
  const copyrightContent = buildCopyrightPageContent(input.copyrightPage, {
    ...input.metadata,
    title,
    subtitle,
    author,
  });
  const dedicationContent = buildDedicationPageContent(input.dedicationPage);
  return {
    titlePage: { title, subtitle, author },
    copyright: mapMaybe(copyrightContent, (paragraphs) => ({
      paragraphs: paragraphs.map((p) => ({
        text: p.text,
        kind: p.kind,
      })),
    })),
    dedication: mapMaybe(dedicationContent, (paragraphs) => ({
      paragraphs: [...paragraphs],
    })),
  };
}

function mapExportImageToPdfBookImage(image: MapExportImage): PdfBookImage {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < image.bytes.length; i += chunk) {
    binary += String.fromCharCode(...image.bytes.subarray(i, i + chunk));
  }
  return {
    mime: "image/png",
    bytesBase64: btoa(binary),
    widthPx: image.widthPx,
    heightPx: image.heightPx,
  };
}

/** Convert a prepared map figure into a PdfBook map leaf. */
export function preparedMapToPdfBookMap(
  prepared: PreparedMapFigure,
): PdfBookMap {
  const print = mapImagesForPrint(prepared);
  if (print.kind === "spread") {
    return {
      type: "map",
      title: prepared.title,
      printExtent: prepared.printExtent,
      objectFit: prepared.objectFit,
      pages: {
        kind: "spread",
        verso: mapExportImageToPdfBookImage(print.verso),
        recto: mapExportImageToPdfBookImage(print.recto),
      },
    };
  }
  return {
    type: "map",
    title: prepared.title,
    printExtent: prepared.printExtent,
    objectFit: prepared.objectFit,
    pages: {
      kind: "single",
      image: mapExportImageToPdfBookImage(print.image),
    },
  };
}

/** Build the PDF book model (front matter + chapters + maps). */
export function buildPdfBook(input: BuildPdfBookInput): PdfBook {
  const page = pdfBookPageSize(input.printSize);
  const body: PdfBookLeaf[] = input.leaves.map((leaf) => {
    switch (leaf.type) {
      case "chapter":
        return chapterToPdfBookChapter(
          leaf.chapter,
          leaf.scenes,
          input.sceneFormatting,
          input.paragraphFormatting,
          input.chapterHeadingStyle,
        );
      case "map":
        return preparedMapToPdfBookMap(leaf.prepared);
      default:
        return assertNever(leaf);
    }
  });
  return {
    title: input.title,
    author: input.author,
    printSize: input.printSize,
    page,
    marginPt: BODY_MARGIN_IN * 72,
    fonts: {
      bodyId: input.bodyFontId,
      headingId: input.headingFontId,
    },
    pageChrome: input.pageChrome,
    frontMatter: buildPdfBookFrontMatter({
      title: input.title,
      author: input.author,
      subtitle: input.subtitle ?? "",
      metadata: input.metadata,
      copyrightPage: input.copyrightPage,
      dedicationPage: input.dedicationPage,
    }),
    body,
  };
}

export function assertPdfBookBlock(block: PdfBookBlock): void {
  switch (block.type) {
    case "paragraph":
    case "sceneBreak":
    case "heading":
      return;
    default:
      assertNever(block);
  }
}

/** JSON for Rust: Maybe values become `null` at the wire edge. */
export function pdfBookToNativeJson(book: PdfBook): string {
  const wire = {
    ...book,
    frontMatter: {
      titlePage: book.frontMatter.titlePage,
      copyright: fromMaybe(book.frontMatter.copyright),
      dedication: fromMaybe(book.frontMatter.dedication),
    },
    body: book.body.map((leaf) => {
      switch (leaf.type) {
        case "chapter":
          return {
            ...leaf,
            heading: {
              ...leaf.heading,
              image: fromMaybe(leaf.heading.image),
            },
          };
        case "map":
          return leaf;
        default:
          return assertNever(leaf);
      }
    }),
  };
  return JSON.stringify(wire);
}
